import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { toThunkRejectValue } from "../../../Common/isRequestCanceled";
import {
  BASE_URL,
  NEW_CUSTOMER_REPORT,
  NEW_CUSTOMER_REPORT_COUNT,
  NEW_CUSTOMER_REPORT_SALES_AMOUNT,
} from "../../../Constants/Config";
import { useAuthDetails } from "../../../Common/cookiesHelper";

let abortController = null;
let abortControllerCount = null;
let abortControllerSales = null;

const initialState = {
  loading: false,
  newCustomerreportData: [],
  successMessage: "",
  error: "",

  countLoading: false,
  countData: 10,
  customerCountData: [],
  customerCountDataAll: [],
  SaleAmountLoading: false,
  customerSaleAmountData: [],
};

// Generate pending, fulfilled, and rejected action types
export const fetchnewCustomerreportData = createAsyncThunk(
  "newCustomerreport/fetchnewCustomerreportData",
  async (data, { rejectWithValue }) => {
    if (abortController) {
      abortController.abort();
    }

    abortController = new AbortController();
    const signal = abortController.signal;

    const { userTypeData } = useAuthDetails();
    try {
      const { token, ...otherUserData } = userTypeData;
      const response = await axios.post(
        BASE_URL + NEW_CUSTOMER_REPORT,
        { ...data, ...otherUserData },
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
          signal,
        }
      );
      if (response?.data?.status === "Success") {
        return response.data.result || [];
      }
      // Empty / "no data" responses are a valid result, not an error.
      return [];
    } catch (error) {
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

// Generate pending, fulfilled, and rejected action types for count
export const fetchnewCustomerreportDataCount = createAsyncThunk(
  "products/fetchnewCustomerreportDataCount",
  async (payload, { rejectWithValue }) => {
    if (abortControllerCount) {
      abortControllerCount.abort();
    }

    abortControllerCount = new AbortController();
    const signal = abortControllerCount.signal;

    const { token, ...dataNew } = payload;
    try {
      const response = await axios.post(
        BASE_URL + NEW_CUSTOMER_REPORT_COUNT,
        dataNew,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
          signal,
        }
      );
      if (response?.data?.status) {
        return response.data;
      }
      // Falsy status (no rows) must still return a payload so the
      // fulfilled reducer can destructure it without throwing.
      return {
        customer_count_data: [],
        total_count: 0,
      };
    } catch (error) {
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

export const fetchnewCustomerreportDataSales = createAsyncThunk(
  "products/fetchnewCustomerreportDataSales",
  async (payload, { rejectWithValue }) => {
    if (abortControllerSales) {
      abortControllerSales.abort();
    }

    abortControllerSales = new AbortController();
    const signal = abortControllerSales.signal;

    const { token, ...dataNew } = payload;
    try {
      const response = await axios.post(
        BASE_URL + NEW_CUSTOMER_REPORT_SALES_AMOUNT,
        dataNew,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
          signal,
        }
      );
      if (response?.data?.status) {
        return response.data.customer_amount_data || [];
      }
      return [];
    } catch (error) {
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

const newCustomerReportSlice = createSlice({
  name: "newCustomerreport",
  initialState,
  extraReducers: (builder) => {
    builder.addCase(fetchnewCustomerreportData.pending, (state) => {
      state.loading = true; // Set loading to true when a new request is initiated
      // console.log("state.loading", state.loading);
    });
    builder.addCase(fetchnewCustomerreportData.fulfilled, (state, action) => {
      state.loading = false;
      state.newCustomerreportData = action.payload || [];
      state.error = "";
    });
    builder.addCase(fetchnewCustomerreportData.rejected, (state, action) => {
      // console.log("Rejected action:", action);
      // state.loading = false;
      // state.newCustomerreportData = {};
      // state.error = action.error.message;
      if (action.payload && action.payload.message === "canceled") {
        // If the request was canceled, don't change loading state
        state.error = "Request was canceled";
        // Optionally, keep the data intact or reset it if needed
        // state.newCustomerreportData = []; // Uncomment if you want to reset data on cancel
      } else {
        // For other errors, set loading to false and handle the error
        state.loading = false;
        state.newCustomerreportData = [];
        state.error = action.payload
          ? action.payload.status || action.error.message
          : "Network Error";
      }
    });

    builder.addCase(fetchnewCustomerreportDataCount.pending, (state) => {
      state.countLoading = true;
      state.countData = [];
      state.customerCountData = [];
      state.customerCountDataAll = [];
    });
    builder.addCase(
      fetchnewCustomerreportDataCount.fulfilled,
      (state, action) => {
        const { customer_count_data, total_count, ...rest } =
          action?.payload || {};
        state.countLoading = false;
        state.countData = total_count || 0;
        state.customerCountData = customer_count_data || [];
        state.customerCountDataAll = rest || [];
      }
    );
    builder.addCase(
      fetchnewCustomerreportDataCount.rejected,
      (state, action) => {
        state.countLoading = false;
        state.countData = [];
        state.customerCountData = [];
        state.customerCountDataAll = [];
      }
    );

    builder.addCase(fetchnewCustomerreportDataSales.pending, (state) => {
      state.SaleAmountLoading = true;
      state.customerSaleAmountData = [];
    });
    builder.addCase(
      fetchnewCustomerreportDataSales.fulfilled,
      (state, action) => {
        state.SaleAmountLoading = false;
        state.customerSaleAmountData = action.payload;
      }
    );
    builder.addCase(
      fetchnewCustomerreportDataSales.rejected,
      (state, action) => {
        state.SaleAmountLoading = false;
        state.customerSaleAmountData = [];
      }
    );
  },
});

export default newCustomerReportSlice.reducer;
